import { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Check, Copy, Smartphone } from 'lucide-react'
import { useIsTouch } from '../hooks/useIsTouch'
import { copyText } from '../lib/clipboard'

/** Group a base32 setup key in fours so it can be read and typed (D-108). */
export function groupSetupKey(secret: string): string {
  return secret.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim()
}

/**
 * Authenticator pairing (D-108).
 *
 * A **phone cannot scan a QR code shown on its own screen**, so the phone gets a
 * pairing path that does not need a camera: an `otpauth://` deep link that opens the
 * authenticator app with the account already filled in, plus the setup key in
 * grouped form with a real copy button. The QR stays — it is still the right thing
 * when pairing from a different device — and the desktop instructions are unchanged.
 *
 * The `otpauth://` URI is only ever used as a link target: it is never printed as
 * text, and the secret never leaves this component or reaches a third-party QR
 * image service.
 */
export function TwoFactorQr({
  otpauthUri,
  secret,
  account,
  touch,
}: {
  otpauthUri: string
  secret: string
  account?: string
  /** Test seam: render the phone path without a real touch screen. */
  touch?: boolean
}) {
  const detectedTouch = useIsTouch()
  const onPhone = touch ?? detectedTouch
  const [showKey, setShowKey] = useState(false)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const groupedKey = groupSetupKey(secret)
  const label = account ? `Salsa Shop Store OS · ${account}` : 'Salsa Shop Store OS'
  // On a phone the key is the fallback path, so it is on screen from the start.
  const keyVisible = onPhone || showKey

  useEffect(() => {
    if (copyState === 'idle') return
    const timer = window.setTimeout(() => setCopyState('idle'), 4000)
    return () => window.clearTimeout(timer)
  }, [copyState])

  const copy = () => {
    void copyText(secret).then((ok) => setCopyState(ok ? 'copied' : 'failed'))
  }

  return (
    <div className="space-y-3">
      <ol className="space-y-1 text-xs text-slate-600 dark:text-slate-400">
        {onPhone ? (
          <>
            <li>
              1. Open your authenticator app with the button below — it fills in “{label}”
              automatically.
            </li>
            <li>
              2. If nothing opens, copy the setup key and paste it into the app as a setup
              key.
            </li>
            <li>3. Type the 6-digit code the app shows into the field underneath.</li>
          </>
        ) : (
          <>
            <li>1. Open your authenticator app — Google Authenticator, Microsoft Authenticator, 1Password…</li>
            <li>2. Scan the code below; the app adds “{label}”.</li>
            <li>3. Type the 6-digit code the app shows into the field underneath.</li>
          </>
        )}
      </ol>

      {onPhone && (
        <a
          href={otpauthUri}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-salsa-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-salsa-600"
        >
          <Smartphone className="h-4 w-4" aria-hidden="true" />
          Open in authenticator app
        </a>
      )}

      <div className="flex justify-center">
        <div className="w-full max-w-[200px] rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10">
          <QRCodeSVG
            value={otpauthUri}
            size={176}
            level="M"
            marginSize={2}
            bgColor="#ffffff"
            fgColor="#0c111b"
            title={label}
            className="h-auto w-full"
          />
        </div>
      </div>

      {onPhone && (
        <p className="text-center text-[11px] text-slate-500 dark:text-slate-400">
          Pairing a second device? Scan this code with its camera.
        </p>
      )}

      {!onPhone && (
        <button
          type="button"
          onClick={() => setShowKey((current) => !current)}
          className="w-full text-center text-xs font-medium text-slate-500 hover:text-salsa-600 dark:text-slate-400"
        >
          {showKey ? 'Hide the setup key' : 'Use a setup key instead of scanning'}
        </button>
      )}

      {keyVisible && (
        <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-white/10 dark:bg-white/5">
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Setup key — paste it into the app, or type it. The spaces are only there to help
            you read it.
          </p>
          <code className="block select-all break-all rounded-lg bg-white px-2 py-1.5 font-mono text-xs tracking-[0.14em] text-slate-800 dark:bg-black/25 dark:text-slate-100">
            {groupedKey}
          </code>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-salsa-600 hover:bg-salsa-500/10 dark:text-salsa-400"
          >
            {copyState === 'copied' ? (
              <Check className="h-3 w-3" aria-hidden="true" />
            ) : (
              <Copy className="h-3 w-3" aria-hidden="true" />
            )}
            {copyState === 'copied' ? 'Copied' : 'Copy setup key'}
          </button>
          {copyState === 'failed' && (
            <p role="status" className="text-[11px] text-amber-700 dark:text-amber-300">
              Copying is blocked in this browser — long-press the key above and choose Copy.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
